import { ROLES } from '@/lib/constants/roles';
import type { PermissionKey } from '@/lib/constants/permissions';
import type { SessionUser } from '@/lib/types/auth.types';

/**
 * The single client-side authorization seam (AUTHZ-1), mirroring the backend
 * `can()`. Gate feature UI (visibility, disabled/readOnly) on this - never on
 * raw role/`canEdit` comparisons. The backend re-checks every request; this is
 * reflection only.
 *
 * Resolution reads the granular `activeMembership.permissions` array the
 * backend resolves live on every `/auth/me` call (role defaults + persisted
 * per-membership overrides) - this is the "when granular permissions arrive in
 * the session, only this function changes" moment this comment used to
 * describe as future work. Super admin still short-circuits to every key.
 */
export function can(user: SessionUser | null, permission: PermissionKey): boolean {
  if (!user) return false;
  if (user.roles.includes(ROLES.SUPER_ADMIN)) return true;
  const membership = user.activeMembership;
  if (!membership) return false;
  return membership.permissions.includes(permission);
}

/**
 * Check several permission keys at once, returning a result per key. Useful
 * anywhere multiple `can()` calls would otherwise be made in a row (e.g. a
 * future admin screen rendering one toggle column per permission per row).
 */
export function canAll(
  user: SessionUser | null,
  keys: readonly PermissionKey[],
): Record<PermissionKey, boolean> {
  return Object.fromEntries(keys.map((key) => [key, can(user, key)])) as Record<
    PermissionKey,
    boolean
  >;
}

/**
 * True when the user holds ANY of the given keys - mirrors the backend's
 * `RequirePermission`/`PermissionGuard` ANY-of semantics for a single value
 * that may be one key or several (e.g. a nav item/route reachable by either
 * the broad `team.manage` capability or the narrower `team.invite` one).
 * Accepts a bare key too so callers don't need to wrap a single-key case in
 * an array.
 */
export function canAny(
  user: SessionUser | null,
  permission: PermissionKey | readonly PermissionKey[],
): boolean {
  const keys = Array.isArray(permission) ? permission : [permission];
  return keys.some((key) => can(user, key));
}
